import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BUSINESS_TYPES_EN, BUSINESS_TYPES_FR } from "@/i18n/translations";
import { trackLeadSubmit } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const LeadMagnet = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", business_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.business_type) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, locale: lang });
      trackLeadSubmit(form.business_type);
      toast.success(t.lead.success);
      setSubmitted(true);
      setForm({ name: "", email: "", business_type: "", message: "" });
    } catch (err) {
      toast.error(t.lead.error);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const types = lang === "fr" ? BUSINESS_TYPES_FR : BUSINESS_TYPES_EN;

  return (
    <section id="lead" data-testid="lead-magnet-section" className="py-16 md:py-32 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-x-0 -top-32 h-96 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 0%, rgba(212,111,77,0.18), rgba(212,111,77,0) 70%)",
        }}
      />
      <div className="relative max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-center">
          <div className="eyebrow">{t.lead.eyebrow}</div>
          <h2 className="mt-4 font-display font-light text-4xl md:text-5xl tracking-tight text-maif">
            {t.lead.title}
          </h2>
          <p className="mt-5 text-maif-muted max-w-xl mx-auto">{t.lead.desc}</p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="lead-form"
          className="mt-12 rounded-3xl border border-sand-soft bg-maif-2 p-6 md:p-10 backdrop-blur-md"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className="eyebrow">{t.lead.name}</label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={form.name}
                onChange={onChange}
                data-testid="lead-input-name"
                className="input-dark mt-2"
                placeholder={lang === "fr" ? "Sara Bensaid" : "Sara Bensaid"}
              />
            </div>
            <div>
              <label htmlFor="email" className="eyebrow">{t.lead.email}</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={onChange}
                data-testid="lead-input-email"
                className="input-dark mt-2"
                placeholder="sara@company.com"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="business_type" className="eyebrow">{t.lead.business}</label>
              <select
                id="business_type"
                name="business_type"
                required
                value={form.business_type}
                onChange={onChange}
                data-testid="lead-input-business"
                className="input-dark mt-2 appearance-none cursor-pointer"
              >
                <option value="" disabled>
                  {lang === "fr" ? "Sélectionnez…" : "Select…"}
                </option>
                {types.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="message" className="eyebrow">{t.lead.message}</label>
              <textarea
                id="message"
                name="message"
                rows={3}
                value={form.message}
                onChange={onChange}
                data-testid="lead-input-message"
                className="input-dark mt-2 resize-none"
                placeholder={lang === "fr" ? "Quelques lignes sur vos opérations…" : "A few lines about your operations…"}
              />
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <span className="text-xs text-maif-muted/80">{t.lead.privacy}</span>
            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-submit"
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? t.lead.submitting : submitted ? t.lead.success : t.lead.submit}
              {!submitting && <ArrowUpRight size={16} />}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default LeadMagnet;
